import { FeatureFlagResults } from '@moda/portal-stanchions';
import { CONFIG } from '../config';
import { UseUserQuery } from '../generated/types';

type DatadogRumModule = typeof import('@datadog/browser-rum');

type User = NonNullable<UseUserQuery['user']>;

type SyncDatadogRumArgs = {
  consentGranted: boolean;
  flags?: FeatureFlagResults;
  user?: User;
};

const DATADOG_RUM_SERVICE = 'discovery';

export const DATADOG_RUM_ENABLED_USER_IDS_FLAG = 'datadog-rum-enabled-user-ids';

let rumInitialized = false;
let replayRecording = false;
let datadogRumModule: DatadogRumModule | undefined;

const isConfigValuePresent = (value: string | null | undefined) => value != null && value !== '';

const normalizeUserIds = (userIds: (string | number)[]) =>
  Array.from(new Set(userIds.map(userId => String(userId).trim()).filter(Boolean)));

export const parseDatadogRumEnabledUserIds = (treatment: unknown) => {
  if (Array.isArray(treatment)) {
    return normalizeUserIds(
      treatment.filter(
        (item): item is string | number => typeof item === 'string' || typeof item === 'number'
      )
    );
  }

  if (typeof treatment === 'string') {
    return normalizeUserIds(treatment.split(/[\n,]/));
  }

  if (typeof treatment === 'number') {
    return normalizeUserIds([treatment]);
  }

  return [];
};

export const getDatadogRumEnabledUserIds = (flags?: FeatureFlagResults) =>
  parseDatadogRumEnabledUserIds(flags?.[DATADOG_RUM_ENABLED_USER_IDS_FLAG]?.value);

export const isDatadogRumEnabledForUserId = (
  userId: string | null | undefined,
  flags?: FeatureFlagResults
) => {
  if (userId == null || userId === '') return false;

  const enabledUserIds = getDatadogRumEnabledUserIds(flags);

  return enabledUserIds.length > 0 && enabledUserIds.includes(userId);
};

const isDatadogRumConfigured = () =>
  [
    CONFIG.DATADOG_RUM_APPLICATION_ID,
    CONFIG.DATADOG_RUM_CLIENT_TOKEN,
    CONFIG.DATADOG_RUM_SITE
  ].every(isConfigValuePresent);

const getUserName = (user: User) => {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();

  return fullName === '' ? undefined : fullName;
};

const getDatadogRumUser = (user: User) => ({
  id: user.id as string,
  email: user.email ?? undefined,
  name: getUserName(user),
  clientTier: user.clientTier ?? undefined,
  countryCode: user.countryCode || undefined,
  isMember: user.isMember ?? undefined,
  modaPrivate: user.modaPrivate ?? undefined
});

const loadDatadogRum = async () => {
  if (datadogRumModule) return datadogRumModule;

  datadogRumModule = await import(/* webpackChunkName: "datadog-rum" */ '@datadog/browser-rum');

  return datadogRumModule;
};

const initializeDatadogRum = async () => {
  const { datadogRum, DefaultPrivacyLevel } = await loadDatadogRum();

  if (rumInitialized) return datadogRum;

  datadogRum.init({
    applicationId: CONFIG.DATADOG_RUM_APPLICATION_ID,
    clientToken: CONFIG.DATADOG_RUM_CLIENT_TOKEN,
    site: CONFIG.DATADOG_RUM_SITE,
    service: DATADOG_RUM_SERVICE,
    env: CONFIG.ENVIRONMENT || CONFIG.NODE_ENV,
    version: CONFIG.TAG || undefined,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    startSessionReplayRecordingManually: true,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: DefaultPrivacyLevel.MASK_USER_INPUT,
    trackingConsent: 'granted',
    silentMultipleInit: true
  });

  rumInitialized = true;

  return datadogRum;
};

const disableDatadogRum = () => {
  if (!rumInitialized || !datadogRumModule) return false;

  datadogRumModule.datadogRum.stopSessionReplayRecording();
  datadogRumModule.datadogRum.clearUser();
  datadogRumModule.datadogRum.setTrackingConsent('not-granted');
  datadogRumModule.datadogRum.stopSession();
  replayRecording = false;

  return true;
};

export const syncDatadogRum = async ({
  consentGranted,
  flags = window.__FEATURE_FLAGS__ || {},
  user
}: SyncDatadogRumArgs) => {
  const shouldEnableRum =
    consentGranted &&
    isDatadogRumConfigured() &&
    user?.id != null &&
    isDatadogRumEnabledForUserId(user.id, flags);

  if (!shouldEnableRum || user?.id == null) {
    disableDatadogRum();
    return false;
  }

  const datadogRum = await initializeDatadogRum();

  datadogRum.setTrackingConsent('granted');
  datadogRum.setUser(getDatadogRumUser(user));

  if (!replayRecording) {
    datadogRum.startSessionReplayRecording();
    replayRecording = true;
  }

  return true;
};
